export const generateAssetReportHtml = (client, devices, liveData, columns = {}) => {
    const cols = {
        etiqueta: true,
        hostname: true,
        cpu: true,
        ram: true,
        gpu: true,
        disks: true,
        security: true,
        ip: true,
        ...columns
    };

    // Helper function to check if device is online
    const isDeviceOnline = (device) => {
        const id = device?.id;
        if (!id) return false;
        if (liveData[id] && liveData[id].online === true) {
            const lastUpdate = liveData[id].lastUpdate;
            if (lastUpdate) {
                const updateTime = typeof lastUpdate === 'string' ? new Date(lastUpdate).getTime() : lastUpdate;
                const diffSeconds = (Date.now() - updateTime) / 1000;
                if (diffSeconds > 40) return false;
            }
            return true;
        }
        return false;
    };

    let html = `
    <html>
    <head>
        <title>Relatório de Ativos - ${client?.nomeFantasia || client?.name || 'Cliente'}</title>
        <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
        <style>
            @page { size: landscape; margin: 10mm; }
            body { font-family: 'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; padding: 0; color: #334155; background: #f8fafc; margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            
            .report-container { padding: 30px; background: #fff; min-height: 100vh; }
            
            .header-block { display: flex; justify-content: space-between; align-items: flex-end; padding-bottom: 20px; border-bottom: 4px solid #06b6d4; margin-bottom: 30px; }
            .header-content h1 { color: #0f172a; margin: 0 0 6px 0; font-size: 26px; font-weight: 800; display: flex; align-items: center; gap: 12px; letter-spacing: -0.5px; }
            .header-content h1 span { color: #06b6d4; font-weight: 400; }
            .header-content .meta { color: #64748b; font-size: 13px; font-weight: 500; }
            
            .logo-placeholder { font-size: 28px; font-weight: 900; color: #0f172a; letter-spacing: -1.5px; }
            .logo-placeholder span { color: #06b6d4; }
            
            table { width: 100%; border-collapse: separate; border-spacing: 0; font-size: 11px; margin-bottom: 20px; box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06); border-radius: 8px; overflow: hidden; border: 1px solid #e2e8f0; }
            tr { page-break-inside: avoid; }
            th { background-color: #0f172a; color: #f8fafc; font-weight: 600; text-align: left; padding: 8px 6px; text-transform: uppercase; font-size: 10px; letter-spacing: 0.5px; border-bottom: 3px solid #06b6d4; }
            td { padding: 8px 6px; border-bottom: 1px solid #e2e8f0; vertical-align: top; background: #fff; color: #334155; line-height: 1.4; }
            tr:nth-child(even) td { background-color: #f8fafc; }
            tr:last-child td { border-bottom: none; }
            
            .status-online { color: #10b981; font-weight: 700; font-size: 10px; display: inline-flex; align-items: center; gap: 4px; }
            .status-offline { color: #94a3b8; font-weight: 700; font-size: 10px; display: inline-flex; align-items: center; gap: 4px; }
            
            .badge { display: inline-block; padding: 2px 5px; border-radius: 4px; font-size: 9px; font-weight: 700; margin-bottom: 3px; border: 1px solid transparent; text-transform: uppercase; letter-spacing: 0.5px; }
            .badge-success { background: #ecfdf5; color: #059669; border-color: #a7f3d0; }
            .badge-warning { background: #fffbeb; color: #d97706; border-color: #fde68a; }
            .badge-danger { background: #fef2f2; color: #dc2626; border-color: #fecaca; }
            .badge-info { background: #f0f9ff; color: #0284c7; border-color: #bae6fd; }
            
            .list-item { margin-bottom: 4px; }
            .list-item:last-child { margin-bottom: 0; }
            .list-item strong { color: #0f172a; font-weight: 600; }
            .sub-text { font-size: 10px; color: #64748b; margin-top: 1px; display: block; }
            
            .disk-bar-container { background-color: #e2e8f0; border-radius: 4px; height: 5px; width: 100%; margin-top: 3px; overflow: hidden; }
            .disk-bar-fill { background-color: #06b6d4; height: 100%; }
            .disk-bar-fill.danger { background-color: #ef4444; }

            @media print {
                body { background: #fff; }
                .report-container { padding: 0; box-shadow: none; }
                table { box-shadow: none; border: 1px solid #cbd5e1; }
                th { background-color: #0f172a !important; color: #fff !important; -webkit-print-color-adjust: exact; }
                tr:nth-child(even) td { background-color: #f8fafc !important; -webkit-print-color-adjust: exact; }
                .badge-success { background: #ecfdf5 !important; border-color: #a7f3d0 !important; }
                .badge-warning { background: #fffbeb !important; border-color: #fde68a !important; }
                .badge-danger { background: #fef2f2 !important; border-color: #fecaca !important; }
                .disk-bar-container { background-color: #e2e8f0 !important; }
                .disk-bar-fill { background-color: #06b6d4 !important; }
                .disk-bar-fill.danger { background-color: #ef4444 !important; }
                .header-block { border-bottom: 4px solid #06b6d4 !important; padding-bottom: 10px !important; margin-bottom: 15px !important; }
                .print-only { display: block; }
                .screen-only { display: none; }
            }
            .print-only { display: none; }
            .screen-only { display: block; }
        </style>
    </head>
    <body>
        <div class="report-container">
            <div class="header-block">
                <div class="header-content">
                    <h1>Gestão de Ativos <span>|</span> ${client?.nomeFantasia || client?.name || 'Cliente'}</h1>
                    <div class="meta">Gerado em: ${new Date().toLocaleString('pt-BR')} • ${devices.length} dispositivo(s) listado(s)</div>
                </div>
                <div class="logo-placeholder">
                    Arcus Asset Management<span>.</span>
                </div>
            </div>
             `;

    const chunks = [];
    for (let i = 0; i < devices.length; i += 4) {
        chunks.push(devices.slice(i, i + 4));
    }

    const colgroupHtml = `
        <colgroup>
            <col style="width: 3%;">
            ${cols.etiqueta ? '<col style="width: 8%;">' : ''}
            ${cols.hostname ? '<col style="width: 12%;">' : ''}
            ${cols.cpu ? '<col style="width: 14%;">' : ''}
            ${cols.ram ? '<col style="width: 10%;">' : ''}
            ${cols.gpu ? '<col style="width: 12%;">' : ''}
            ${cols.disks ? '<col style="width: 14%;">' : ''}
            ${cols.security ? '<col style="width: 17%;">' : ''}
            ${cols.ip ? '<col style="width: 10%;">' : ''}
        </colgroup>
    `;

    const theadHtml = `
        <thead>
            <tr>
                <th style="text-align: center;">#</th>
                ${cols.etiqueta ? '<th>Etiqueta Arcus</th>' : ''}
                ${cols.hostname ? '<th>Hostname</th>' : ''}
                ${cols.cpu ? '<th>Processador</th>' : ''}
                ${cols.ram ? '<th>Memória RAM</th>' : ''}
                ${cols.gpu ? '<th>Placa de Vídeo</th>' : ''}
                ${cols.disks ? '<th>Discos</th>' : ''}
                ${cols.security ? '<th>Maturidade de Segurança</th>' : ''}
                ${cols.ip ? '<th>IP</th>' : ''}
            </tr>
        </thead>
    `;

    const getRowHtml = (d, index) => {
        const isOnline = isDeviceOnline(d);

        // 1. Etiqueta Arcus
        const etiqueta = d.arcus_label ? `<strong style="font-size: 12px; color: #0f172a;">${d.arcus_label}</strong>` : '<span style="color: #94a3b8;">-</span>';

        // 2. Hostname
        const statusHtml = isOnline ? '<span class="status-online">● ONLINE</span>' : '<span class="status-offline">○ OFFLINE</span>';
        const hostnameHtml = `<strong style="font-size: 13px; color: #0f172a;">${d.hostname || 'Desconhecido'}</strong><br/>${statusHtml}`;

        // 3. Processador
        const cpuName = d.cpu?.brand || (typeof d.cpu === 'string' ? d.cpu : '<span style="color: #94a3b8;">Desconhecido</span>');
        const cpuHtml = `<strong style="color: #334155;">${cpuName}</strong>`;

        // 4. Memória RAM
        const liveRam = liveData[d.id]?.ramPercent;
        const ramTotal = parseFloat(d.ram?.totalGB || d.ram_total_gb || 0);
        let ramUsed = d.ram ? (ramTotal - parseFloat(d.ram.freeGB)).toFixed(2) : (d.ram_used_gb || 0);
        if (liveRam !== undefined && ramTotal > 0) {
            ramUsed = ((liveRam / 100) * ramTotal).toFixed(2);
        }
        const ramHtml = ramTotal > 0 ? `
            <strong style="font-size: 12px; color: #0f172a;">${ramTotal.toFixed(1)} GB</strong>
            <span class="sub-text">${ramUsed} GB em uso</span>
        ` : '<span style="color: #94a3b8;">N/A</span>';

        // 5. Placa de Vídeo
        let gpusHtml = '<span style="color: #94a3b8;">-</span>';
        if (liveData[d.id]?.gpus && liveData[d.id].gpus.length > 0) {
            gpusHtml = liveData[d.id].gpus.map(g => `<div class="list-item"><strong>${g.name}</strong></div>`).join('');
        } else if (d.graphics?.controllers && d.graphics.controllers.length > 0) {
            gpusHtml = d.graphics.controllers.map(g => `<div class="list-item"><strong>${g.model || g.name}</strong></div>`).join('');
        }

        // 6. Discos
        let disksHtml = '<span style="color: #94a3b8;">-</span>';
        if (d.disks && d.disks.length > 0) {
            disksHtml = d.disks.map(disk => {
                const total = parseFloat(disk.totalSpaceGB || disk.total_gb || 0);
                const free = parseFloat(disk.freeSpaceGB || disk.free_gb || 0);
                const percentFree = total > 0 ? (free / total) * 100 : 0;
                const percentUsed = 100 - percentFree;
                const barClass = percentFree < 15 ? 'disk-bar-fill danger' : 'disk-bar-fill';

                return `
                <div class="list-item">
                    <strong>${disk.drive} - ${total.toFixed(1)} GB</strong> <span style="font-size: 10px; color: #64748b;">- ${free.toFixed(1)} GB livres</span>
                    <div class="disk-bar-container">
                        <div class="${barClass}" style="width: ${percentUsed}%;"></div>
                    </div>
                </div>`;
            }).join('');
        }

        // 7. Maturidade e Segurança
        let securityHtml = '';
        if (d.security) {
            let score = 0;
            if (d.security.antivirus?.active) score += 25;
            if (d.security.firewall?.state === 'Ativado') score += 25;
            if (d.security.bitlocker?.encrypted) score += 25;
            if (d.security.uac?.enabled) score += 25;

            const scoreColor = score >= 75 ? '#10b981' : (score >= 50 ? '#f59e0b' : '#ef4444');

            const avStatus = d.security.antivirus?.active ? '<span class="badge badge-success">OK</span>' : '<span class="badge badge-danger">ERRO</span>';
            const avName = d.security.antivirus?.name || 'Desconhecido';
            
            const fwState = d.security.firewall?.state;
            const fwStatus = fwState === 'Ativado' ? '<span class="badge badge-success">ATIVO</span>' : '<span class="badge badge-danger">INATIVO</span>';
            
            const bitStatus = d.security.bitlocker?.encrypted ? '<span class="badge badge-success">ATIVO</span>' : '<span class="badge badge-warning">OFF</span>';
            const uacStatus = d.security.uac?.enabled ? '<span class="badge badge-success">ATIVO</span>' : '<span class="badge badge-danger">OFF</span>';

            securityHtml = `
                <div style="display: flex; gap: 8px; align-items: stretch;">
                    <div style="flex: 1;">
                        <div class="list-item" style="display: flex; align-items: center; gap: 4px;"><strong style="width: 72px; flex-shrink: 0;">Antivírus:</strong> ${avStatus}</div>
                        <div class="list-item" style="display: flex; align-items: center; gap: 4px;"><strong style="width: 72px; flex-shrink: 0;">Firewall:</strong> ${fwStatus}</div>
                        <div class="list-item" style="display: flex; align-items: center; gap: 4px;"><strong style="width: 72px; flex-shrink: 0;">BitLocker:</strong> ${bitStatus}</div>
                        <div class="list-item" style="display: flex; align-items: center; gap: 4px;"><strong style="width: 72px; flex-shrink: 0;">UAC:</strong> ${uacStatus}</div>
                    </div>
                    <div style="width: 45px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 4px;">
                        <span style="font-size: 8px; font-weight: 800; color: #64748b; text-transform: uppercase;">Score</span>
                        <span style="font-size: 14px; font-weight: 800; color: ${scoreColor};">${score}%</span>
                    </div>
                </div>
            `;
        } else {
            securityHtml = '<span class="badge badge-warning">Sem dados</span>';
        }

        // 8. IP
        const ipHtml = `<div class="list-item"><strong>Local:</strong><br/>${d.localIp || d.network?.[0]?.ip || '<span style="color: #94a3b8;">N/A</span>'}</div>
                        <div class="list-item" style="margin-top: 6px;"><strong>Público:</strong><br/>${d.public_ip || '<span style="color: #94a3b8;">N/A</span>'}</div>`;

        return `
            <tr>
                <td style="text-align: center; font-weight: 700; color: #64748b; vertical-align: middle;">${index + 1}</td>
                ${cols.etiqueta ? `<td>${etiqueta}</td>` : ''}
                ${cols.hostname ? `<td>${hostnameHtml}</td>` : ''}
                ${cols.cpu ? `<td>${cpuHtml}</td>` : ''}
                ${cols.ram ? `<td>${ramHtml}</td>` : ''}
                ${cols.gpu ? `<td>${gpusHtml}</td>` : ''}
                ${cols.disks ? `<td>${disksHtml}</td>` : ''}
                ${cols.security ? `<td>${securityHtml}</td>` : ''}
                ${cols.ip ? `<td>${ipHtml}</td>` : ''}
            </tr>
        `;
    };

    html += `
        <div class="screen-only">
            <table>
                ${colgroupHtml}
                ${theadHtml}
                <tbody>
                    ${devices.map((d, index) => getRowHtml(d, index)).join('')}
                </tbody>
            </table>
        </div>
        
        <div class="print-only">
            ${chunks.map((chunk, chunkIndex) => `
                <table${chunkIndex > 0 ? ' style="page-break-before: always; break-before: page; margin-top: 20px;"' : ''}>
                    ${colgroupHtml}
                    ${chunkIndex === 0 ? theadHtml : ''}
                    <tbody>
                        ${chunk.map((d, i) => getRowHtml(d, chunkIndex * 4 + i)).join('')}
                    </tbody>
                </table>
            `).join('')}
        </div>
    `;

    html += `
        </div>
    </body>
    </html>
    `;

    return html;
};

